import {GeneralService} from './GeneralService';
import config from '../config/config';
import moment from 'moment';
import {Contract} from '@ethersproject/contracts';
import {BaseProvider, Web3Provider} from '@ethersproject/providers';
import {TokenService} from './TokenService';
import {formatEther, parseEther, parseUnits} from '@ethersproject/units';
import {StakingType} from "./LiquidyMiningService";

export function AmasaStakingService(provider: BaseProvider, stakingType: StakingType) {
    const amasaStakingAddress = process.env.REACT_APP_AMASA_STAKING_ADDRESS || '';
    const amasaSecondStakingAddress = process.env.REACT_APP_AMASA_SECOND_STAKING_ADDRESS || '';
    const amasaStakingContract = new Contract(
        stakingType === StakingType.FIRST ? amasaStakingAddress : amasaSecondStakingAddress,
        config.tangoStakingABI,
        provider instanceof Web3Provider ? provider?.getSigner() : provider,
    );

    return {
        async getStakedBalance(
            accountAddress: string | null,
            makeupBalance: boolean,
        ) {
            let balance = await amasaStakingContract.stakeOf(accountAddress);
            const staked = formatEther(balance.toString());
            if (!makeupBalance) {
                balance =
                    Math.floor(Number(formatEther(balance.toString())) * 10000000) /
                    10000000;
            } else {
                balance = GeneralService().prettyTotalStaked(
                    formatEther(balance.toString()).toString(),
                );
            }

            const data = {
                stakedBalance: staked,
                stakedBalancePretty: balance,
            };
            return data;
        },
        async withdraw() {
            return await amasaStakingContract.withdraw();
        },
        async getTimeStaked(accountAddress: string | null) {
            let timeObj = await amasaStakingContract.timeStaked(accountAddress);
            let time = Number(timeObj.toString());
            if (time === 0) {
                let data = {
                    time: time.toString(),
                    claimEnabled: false,
                    withdrawDate: null,
                    withdrawDateMobile: null,
                    progress: '0',
                    timeStaked: 0
                };
                return data;
            }
            let canWithdrawObj = await amasaStakingContract.canWithdraw(
                accountAddress,
            );
            let canWithdraw = Boolean(canWithdrawObj.toString());
            let depositDate = moment.unix(time);
            const now = Math.floor(new Date().getTime() / 1000);
            let depositTime = await amasaStakingContract.depositTime();
            const stakingDuration = depositTime / (24 * 3600);
            let withdrawalDate = depositDate.add(
                stakingDuration,
                'days',
            ); //add 3 month
            const total = Number(withdrawalDate.unix()) - Number(time);
            const progress = now - Number(time);
            const tempPercentage = (progress / total) * 100; // nest time progress
            const percentage = tempPercentage > 100 ? 100 : tempPercentage;
            const balanceObj = await amasaStakingContract.stakeOf(accountAddress);
            const staked = Number(parseUnits(balanceObj.toString(), 'ether'));
            const nestRewardPercentage = (
                (await amasaStakingContract.rewardPerc()) / 10
            ).toString();

            const totalReward = (staked * Number(nestRewardPercentage)) / 100; //calculate total reward after time period is over
            const progressReward = (totalReward * percentage) / 100; //calculate current progress reward
            let data = {
                time: time,
                claimEnabled: canWithdraw,
                withdrawDate: Number(withdrawalDate.unix()),
                withdrawDateMobile: withdrawalDate.format('DD, MMMM, YYYY h:mm'),
                progress: Number(percentage).toFixed(2),
                progressReward: progressReward.toFixed(6),
                timeStaked: time
            };
            return data;
        },

        async checkApproval(accountAddress: string | null, tokenAddress: string) {
            let balance = await TokenService(provider).geBalance(accountAddress, tokenAddress);
            balance = parseUnits(balance.toString(), 'ether');

            const allowance = await TokenService(provider).allowance(
                accountAddress,
                stakingType === StakingType.FIRST ? amasaStakingAddress : amasaSecondStakingAddress,
                tokenAddress
            );
            const ethAllowance = parseUnits(allowance.toString(), 'ether');
            if (
                parseFloat(ethAllowance.toString()) >= parseFloat(balance.toString())
            ) {
                return true;
            } else {
                return false;
            }
        },
        async stake(stakeAmount: string) {
            const stakeAmountNum = parseUnits(stakeAmount, 'ether');
            return await amasaStakingContract.stake(stakeAmountNum);
        },
        async getContractInfo() {
            let data = {
                stakingDuration: 0,
                stakingCapacity: '',
                stakedTotal: '',
                stakingStarts: 0,
                stakingEnds: 0,
                rewardPerc: '',
                poolStatus: ''
            };
            let depositTime = await amasaStakingContract.depositTime();
            let stakingCapacity = await amasaStakingContract.stakingCap();
            data.stakedTotal = (await amasaStakingContract.stakedTotal()).toString();
            data.stakingStarts = Number((await amasaStakingContract.stakingStarts()).toString());
            data.stakingEnds = Number((await amasaStakingContract.stakingEnds()).toString());
            data.rewardPerc = ((await amasaStakingContract.rewardPerc()) / 10).toString();

            data.stakingDuration = depositTime / (24 * 3600);
            data.stakingCapacity = (
                Number(formatEther(stakingCapacity)) / 1000
            ).toString();
            const now = moment().unix();
            if (Number(formatEther(data.stakedTotal)) / 1000 < Number(data.stakingCapacity) && now >= data.stakingStarts && now <= data.stakingEnds) {
                data.poolStatus = 'OPEN';
            } else {
                data.poolStatus = 'CLOSED';
            }

            return data;
        },
    };
}
